import _getValue from 'get-value';
import _mergeOptions from 'merge-options';
import _setValue from 'set-value';

export interface MergeOptions {
  /** 合并对象时是否将数组拼接在一起 */
  concatArrays?: boolean;

  /** 合并对象时是否忽略值为 undefined 的 key */
  ignoreUndefined?: boolean;
}

/**
 * 通过字符串的属性路径 (类似 "a.b.c") 从深层嵌套的对象中安全地取值
 * @description 此函数来自第三方库 [get-value](https://github.com/jonschlinkert/get-value)
 * @example
 * ```js
 * const obj = { a: { b: { c: { d: "foo" } } } };
 * getValue(obj);              // -> { a: { b: { c: { d: "foo" } } } }
 * getValue(obj, "a");         // -> { b: { c: { d: "foo" } } }
 * getValue(obj, "a.b"))       // -> { c: { d: "foo" } }
 * getValue(obj, "a.b.c");     // -> { d: "foo" }
 * getValue(obj, "a.b.c.d");   // -> "foo"
 * getValue({ "a.b": { c: "d" } }, "a.b.c");   // -> "d"
 * getValue({ "a.b": { c: { "d.e": "f" } } }, "a.b.c.d.e");      // -> "f"
 * getValue({ a: { b: [{ c: "d" }, { e: "f" }] } }, "a.b.1.e");  // -> "f"
 * ```
 */
export const getValue = _getValue;

/**
 * 通过字符串的属性路径 (类似 "a.b.c") 从设置对象的深层属性, 或是创建深层嵌套的对象
 * @returns 返回修改后的对象
 * @description 此函数来自第三方库 [set-value](https://github.com/jonschlinkert/set-value)
 * @example
 * ```js
 * const obj = { a: { b: { c: { d: "foo" } } } };
 * setValue(obj, "a.b.c", "bar") === obj;   // -> true
 * setValue(obj, "a.b.c", "bar");           // -> { a: { b: { c: "bar" } } }
 * setValue({}, "a.b.c", "d");              // -> { a: { b: { c: "d" } } }
 * setValue({}, "a\\.b.c", "d");            // -> { "a.b": { c: "d" } }
 * ```
 */
export const setValue = _setValue;

/**
 * 深度合并一个或多个 "选项对象", 合并时后面的参数有更高的优先级 (类似 Object.assign)
 *
 * 只有当嵌套的对象是 Plain Object 的情况下才会去遍历它里面的内容, 否则把它当成普通的值使用
 * @returns 返回一个新的对象, 其内容是合并后对象 (意思是所有的参数都不会被修改)
 * @description 此函数来自第三方库 [merge-options](https://github.com/blend/promise-utils) ([source](https://github.com/blend/promise-utils/blob/master/src/delay.ts))
 * @example
 * ```js
 * mergeOptions({ foo: 0 }, { bar: 1 }, { baz: 2 }, { bar: 3 });  // -> { foo: 0, bar: 3, baz: 2 }
 * mergeOptions({ nested: {unicorns: "none" } }, { nested: { unicorns: "many" } });   // -> { nested: { unicorns: "many" } }
 * mergeOptions.call({ concatArrays: true }, { a: ["foo"] }, { a: ["bar"] });         // -> { a: ["foo", "bar"] }
 * mergeOptions.call({ ignoreUndefined: true }, { foo: "bar" }, { foo: undefined });  // -> { foo: "bar" }
 * ```
 */
export const mergeOptions: (this: MergeOptions | undefined, ...args: any[]) => any = _mergeOptions;
